import React, { useState } from "react";
import { UseLobbyReturn } from "../../../../hooks/useLobby";
import { api } from "../../../../services/api";
import EmptyState from "../../../../components/EmptyState";
import PlayerList from "./PlayerList";
import LobbyStateBadge from "./LobbyStateBadge";
import HostControls from "./HostControls";
import DiscordLink from "./DiscordLink";
import DiscordErrorBanner from "./DiscordErrorBanner";
import RetryDiscordButton from "./RetryDiscordButton";
import styles from "./LobbyView.module.scss";

interface LobbyViewProps {
  lobbyState: UseLobbyReturn;
}

const LobbyView: React.FC<LobbyViewProps> = ({ lobbyState }) => {
  const { lobby, loading, error, isHost, discordError, clearDiscordError, validationFailure, clearValidationFailure, refresh } = lobbyState;
  const [leaving, setLeaving] = useState(false);

  if (loading) {
    return <div className={styles.loading}><p>Loading lobby...</p></div>;
  }

  if (error) {
    return <div className={styles.error}><p>{error}</p></div>;
  }

  if (!lobby) {
    return (
      <EmptyState
        heading="No active lobby"
        description="Create a listing or join one from the Browse tab."
      />
    );
  }

  const handleLeave = async () => {
    setLeaving(true);
    try {
      await api.lobby.leaveLobby(lobby.lobbyId);
      refresh();
    } catch {
      setLeaving(false);
    }
  };

  const isTerminal = lobby.state === "COMPLETED" || lobby.state === "CANCELLED";
  const channelFailed = lobby.discord?.channelCreationFailed === true;

  const getDiscordErrorBannerProps = () => {
    if (!discordError) return null;

    const { code, message, affectedUserIds } = discordError;

    switch (code) {
      case "DISCORD_BOT_UNAVAILABLE":
        return {
          severity: "warning" as const,
          code,
          message: "Discord bot is temporarily offline. Voice chat will be available once it reconnects.",
        };
      case "PLAYERS_MISSING_DISCORD": {
        const affectedUsers = affectedUserIds
          ?.map((uid) => {
            const member = lobby.members.find((m) => m.userId === uid);
            return member ? { userId: uid, username: member.username } : { userId: uid, username: uid };
          }) ?? [];

        return {
          severity: "error" as const,
          code,
          message: "Some players no longer have Discord linked.",
          affectedUsers,
          ...(isHost && {
            action: {
              label: "Remove Players",
              onClick: () => {
                affectedUserIds?.forEach((uid) => {
                  api.lobby.kickPlayer(lobby.lobbyId, uid);
                });
              },
            },
          }),
        };
      }
      case "CHANNEL_CREATION_FAILED":
        return {
          severity: "error" as const,
          code,
          message: "Discord voice channel could not be created",
          ...(isHost && {
            action: {
              label: "Retry",
              onClick: () => {
                api.lobby.retryDiscordChannel(lobby.lobbyId).then(() => refresh());
              },
            },
          }),
        };
      default:
        return {
          severity: "error" as const,
          code,
          message: message || "A Discord error occurred.",
        };
    }
  };

  const bannerProps = getDiscordErrorBannerProps();

  const getValidationBannerProps = () => {
    if (!validationFailure) return null;

    const { reason, affectedUserIds } = validationFailure;

    const affectedUsers = affectedUserIds.map((uid) => {
      const member = lobby!.members.find((m) => m.userId === uid);
      return member ? { userId: uid, username: member.username } : { userId: uid, username: uid };
    });

    const message = reason === "MISSING_DISCORD"
      ? "Players were removed because their Discord accounts are no longer linked"
      : "Players were removed because they are already in an active match";

    return {
      severity: "warning" as const,
      code: "validation_failed",
      message,
      affectedUsers,
    };
  };

  const validationBannerProps = getValidationBannerProps();

  return (
    <div className={styles.lobbyCard}>
      {/* Discord error banner at top of lobby */}
      {bannerProps && (
        <div className={styles.bannerArea}>
          <DiscordErrorBanner
            {...bannerProps}
            onDismiss={clearDiscordError}
          />
        </div>
      )}

      {/* Validation failure banner */}
      {validationBannerProps && (
        <div className={styles.bannerArea}>
          <DiscordErrorBanner
            {...validationBannerProps}
            onDismiss={clearValidationFailure}
          />
        </div>
      )}

      {/* Header with cover art and lobby info */}
      <div className={styles.header}>
        {lobby.gameCoverUrl && (
          <img
            src={lobby.gameCoverUrl}
            alt={lobby.gameName || "Game cover"}
            className={styles.coverArt}
          />
        )}
        <div className={styles.headerInfo}>
          <div className={styles.headerMeta}>
            <LobbyStateBadge state={lobby.state} />
            {lobby.gameName && <span className={styles.gameName}>{lobby.gameName}</span>}
          </div>
          <h2 className={styles.title}>{lobby.title}</h2>
          {lobby.description && (
            <p className={styles.description}>{lobby.description}</p>
          )}
        </div>
      </div>

      {/* Body */}
      <div className={styles.body}>
        <PlayerList
          lobby={lobby}
          isHost={isHost}
          onKick={(userId) => api.lobby.kickPlayer(lobby.lobbyId, userId)}
          onTransferHost={(userId) => api.lobby.transferHost(lobby.lobbyId, userId)}
        />

        {lobby.discordInviteLink && !isTerminal && !channelFailed && (
          <DiscordLink discordInviteLink={lobby.discordInviteLink} />
        )}

        {channelFailed && !isTerminal && (
          <DiscordLink
            discordInviteLink={lobby.discordInviteLink}
            disabled
            disabledMessage="Voice channel unavailable"
          />
        )}

        {isHost && channelFailed && !isTerminal && (
          <RetryDiscordButton
            lobbyId={lobby.lobbyId}
            disabled={false}
            onRetry={refresh}
          />
        )}

        {isHost && !isTerminal && (
          <HostControls lobby={lobby} refresh={refresh} />
        )}

        {!isHost && lobby.state === "OPEN" && (
          <div className={styles.actions}>
            <button
              className={styles.leaveButton}
              onClick={handleLeave}
              disabled={leaving}
            >
              {leaving ? "Leaving..." : "Leave Lobby"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LobbyView;
